import React from "react";
import { type FreefieldCategoryWithValues, type FreefieldCategoryWithValuesAndPermissions, type FreefieldState } from "./models";

// Combines categories and values into a single structure
// Use if you want to render a list of readonly freefields with their values. So if you don't care if a field is editable or read-only
export const useVisibleFreefieldCategoriesWithValues = (
  categories: server.dto.FreeFieldCategorySmall[],
  freefieldValues: server.dto.FreeFieldValue[]
) => {
  return React.useMemo(() =>
    categories.map<FreefieldCategoryWithValues>(cat => ({
      freeFieldCategoryId: cat.freeFieldCategoryId,
      title: cat.title,
      definitionWithValues: cat.definitions
        .map(def => {
          const storedValue = freefieldValues.find(v => v.definitionId === def.freeFieldDefinitionId);
          const useDefaultValue = storedValue == null;
          const displayValue = (useDefaultValue ? def.defaultValue : storedValue?.value) ?? "";
          return {
            ...def,
            storedValue: storedValue?.value,
            displayValue: {
              value: displayValue,
              isDefault: useDefaultValue,
              hasVisibleValue: def.type === "date" ?
                (Boolean(displayValue) && displayValue !== "0") :
                Boolean(displayValue)
            }
          };
        })
    })), [categories, freefieldValues]);
};

// Combines categories, permissions and values into a single structure
// Use in scenarios where you need to know which fields are editable, read-only or hidden
const useFreefieldCategoriesWithValuesAndPermission = (
  categories: server.dto.FreeFieldCategorySmall[],
  freefieldValues: server.dto.FreeFieldValue[],
  freefieldPermission: server.dto.FreeFieldDefinitionPermission[]
) => {
  const ffCategoriesWithValues = useVisibleFreefieldCategoriesWithValues(categories, freefieldValues);

  return React.useMemo(() => ffCategoriesWithValues.map<FreefieldCategoryWithValuesAndPermissions>(cat => ({
    ...cat,
    definitionWithValues: cat.definitionWithValues.map(def => ({
      ...def,
      permission: freefieldPermission.find(p => p.freeFieldDefinitionId === def.freeFieldDefinitionId)!.level
    }))
  })), [ffCategoriesWithValues, freefieldPermission]);
};

// For editing scenarios, we combine:
// - a list of freefields that can be edited and must be submitted to the server
// - all freefields by category & permission, where their value comes from the state for editable fields, and from initial values for read-only fields
export const useFreefieldState = (
  initialValues: server.dto.FreeFieldValue[],
  categories: server.dto.FreeFieldCategorySmall[],
  permissions: server.dto.FreeFieldDefinitionPermission[]
): FreefieldState => {

  // Static list of non-editable freefield values 
  const nonEditableFfValues = React.useMemo(() => initialValues.filter(v => {
    const permission = permissions.find(p => p.freeFieldDefinitionId === v.definitionId);
    return permission?.level !== "viewModify";
  }), [initialValues, permissions]);

  // This state only contains the editable freefield values
  const initialise = () => {
    return initialValues.filter(v => {
      const permission = permissions.find(p => p.freeFieldDefinitionId === v.definitionId);
      return permission?.level === "viewModify";
    });
  };
  const [ffValues, setFfValues] = React.useState<server.dto.FreeFieldValue[]>(initialise);
  const [isInitialised, setIsInitialised] = React.useState(false);

  const combinedFfValues = React.useMemo(() => {
    return [...ffValues, ...nonEditableFfValues];
  }, [ffValues, nonEditableFfValues]);

  // The new structure contains all freefields where:
  // - editable freefield values come from state
  // - read-only freefield values come from the initial values
  const freefieldCategoriesWithValuesAndPermissions = useFreefieldCategoriesWithValuesAndPermission(categories, combinedFfValues, permissions);

  // Helper to update a single editable freefield value
  const setEditableFreefieldValue = (definitionId: number, value: string) => {
    setFfValues(ffValues => {
      const index = ffValues.findIndex(v => v.definitionId === definitionId);
      if (index === -1) {
        return [...ffValues, { definitionId, value }];
      }
      return ffValues.map(v => v.definitionId === definitionId ? { definitionId, value } : v);
    });
  };

  return {
    freefieldCategoriesWithValuesAndPermissions,
    setEditableFreefieldValue,
    editableFreefieldValues: ffValues,
    initialiseOnData: () => {
      if (!isInitialised) {
        setFfValues(initialise());
        setIsInitialised(true);
      }
    },
    refresh: () => {
      setFfValues(initialise());
      setIsInitialised(true);
    }
  };
};
